// ArticleView.jsx — страница одной статьи в /main/articles/:id.
// Алгоритм такой же, как на сайте СтартКласс: подменяем устаревшие домены
// в URL картинок (admin- и rus.online-сабдомены → api-admin).

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderMarkdown } from '../../../utils/markdown';
import AdminService from '../../../API/AdminService';
import { ADMIN_addres, FOLDER_PROFILE } from '../../../API/API';
import UserService from '../../../API/UserService';
import cl from './ArticleView.module.css';
import logger from '../../../utils/logger';
import {
    IconArrowLeft, IconUser, IconCalendar, IconClock, IconEye,
    IconMessage, IconStar, IconChart, IconAlert, IconRefresh,
} from '../../../components/Icons';

// Подмена устаревших доменов в URL картинок (см. FAQArticleView, StartKlass).
function rewriteUrl(url) {
    if (!url) return url;
    return url
        .replace(/https?:\/\/adminpanelminecraftia\.ru\/assets\//gi,
                 'https://minecraftia-school.ru/api-admin/assets/')
        .replace(/https?:\/\/minecraftia-rus\.online\/assets\//gi,
                 'https://minecraftia-school.ru/api-admin/assets/');
}
function resolveImageUrl(raw) {
    if (!raw) return null;
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) {
        return rewriteUrl(raw);
    }
    if (raw.startsWith('/')) return `${ADMIN_addres}${raw}`;
    return `${ADMIN_addres}/assets/articles/${raw}`;
}
function rewriteAllUrls(text) {
    if (!text) return '';
    return rewriteUrl(text);
}

const ArticleView = () => {
    const { articleId } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const [userRating, setUserRating] = useState(0);
    const [showRatingForm, setShowRatingForm] = useState(false);
    const [coverFailed, setCoverFailed] = useState(false);

    const parseMarkdown = renderMarkdown;

    useEffect(() => {
        loadArticle();
    }, [articleId]);

    const loadArticle = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getArticle(articleId);
            setArticle(response.data);
            setComments(response.data.comments || []);
            setError('');
            setCoverFailed(false);
        } catch (err) {
            setError('Ошибка загрузки статьи');
            logger.error('Ошибка загрузки статьи:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        const validationErrors = AdminService.validateCommentData({ content: newComment });
        if (validationErrors.length > 0) {
            alert(validationErrors.join('\n'));
            return;
        }
        try {
            setCommentLoading(true);
            await AdminService.createArticleComment(articleId, { content: newComment });
            setNewComment('');
            await loadArticle();
        } catch (err) {
            logger.error('Ошибка добавления комментария:', err);
            alert('Ошибка добавления комментария');
        } finally {
            setCommentLoading(false);
        }
    };

    const handleRating = async (rating) => {
        try {
            await AdminService.rateArticle(articleId, rating);
            setUserRating(rating);
            setShowRatingForm(false);
            await loadArticle();
        } catch (err) {
            logger.error('Ошибка оценки статьи:', err);
            alert('Ошибка оценки статьи');
        }
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка статьи…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить статью</h3>
                        <p>{error || 'Статья не найдена'}</p>
                        <button onClick={() => navigate('/main/articles')} className={cl.retryButton}>
                            <IconArrowLeft width={14} height={14}/>
                            <span>Вернуться к статьям</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const coverUrl = !coverFailed
        ? (resolveImageUrl(article.featured_image) || resolveImageUrl(article.cover_image_url))
        : null;

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Назад */}
                <button onClick={() => navigate('/main/articles')} className={cl.backLink}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К статьям</span>
                </button>

                {/* Карточка статьи */}
                <article className={cl.articleCard}>
                    {/* Мета сверху */}
                    <div className={cl.articleMeta}>
                        <span className={cl.metaItem}>
                            <IconUser width={12} height={12}/>
                            {article.author_name}
                        </span>
                        <span className={cl.metaItem}>
                            <IconCalendar width={12} height={12}/>
                            {AdminService.formatDate(article.created_at)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconClock width={12} height={12}/>
                            {AdminService.formatReadTime(article.estimated_read_time)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconChart width={12} height={12}/>
                            {AdminService.getDifficultyText(article.difficulty_level)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconEye width={12} height={12}/>
                            {AdminService.formatLargeNumber(article.views_count)}
                            <span className={cl.metaWord}>
                                {pluralRu(article.views_count, 'просмотр', 'просмотра', 'просмотров')}
                            </span>
                        </span>
                        <span className={cl.metaItem}>
                            <IconMessage width={12} height={12}/>
                            {article.comments_count}
                            <span className={cl.metaWord}>
                                {pluralRu(article.comments_count, 'комментарий', 'комментария', 'комментариев')}
                            </span>
                        </span>
                    </div>

                    {/* Заголовок */}
                    <h1 className={cl.title}>{article.title}</h1>

                    {/* Теги */}
                    {article.tags && article.tags.length > 0 && (
                        <div className={cl.tagsRow}>
                            {article.tags.map(tag => (
                                <span key={tag.id} className={cl.tag}>
                                    #{tag.name}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Рейтинг */}
                    <div className={cl.ratingRow}>
                        <span className={cl.ratingValue}>
                            <IconStar width={14} height={14}/>
                            <strong>{AdminService.formatRating(article.rating)}</strong>
                            <span className={cl.ratingCount}>
                                ({article.rating_count} {pluralRu(article.rating_count, 'оценка', 'оценки', 'оценок')})
                            </span>
                        </span>
                        <button
                            onClick={() => setShowRatingForm(!showRatingForm)}
                            className={cl.rateButton}
                        >
                            <IconStar width={13} height={13}/>
                            <span>Оценить</span>
                        </button>
                    </div>

                    {/* Форма оценки */}
                    {showRatingForm && (
                        <div className={cl.ratingForm}>
                            <span className={cl.ratingFormLabel}>Поставьте оценку:</span>
                            <div className={cl.starsContainer}>
                                {[1, 2, 3, 4, 5].map(star => (
                                    <button
                                        key={star}
                                        onClick={() => handleRating(star)}
                                        className={`${cl.starButton} ${star <= userRating ? cl.starActive : ''}`}
                                        aria-label={`Оценить на ${star}`}
                                    >
                                        <IconStar width={20} height={20}/>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Обложка */}
                    {coverUrl && (
                        <div className={cl.coverImage}>
                            <img
                                src={coverUrl}
                                alt={article.title}
                                onError={() => setCoverFailed(true)}
                            />
                        </div>
                    )}

                    {/* Краткое описание */}
                    {article.summary && (
                        <p className={cl.summary}>{article.summary}</p>
                    )}

                    {/* Контент Markdown.
                        Подменяем устаревшие домены в URL картинок до парсинга. */}
                    <div
                        className={cl.content}
                        dangerouslySetInnerHTML={{
                            __html: parseMarkdown(rewriteAllUrls(article.content)),
                        }}
                    />
                </article>

                {/* Комментарии */}
                <section className={cl.commentsSection}>
                    <h2 className={cl.commentsTitle}>
                        Комментарии ({article.comments_count})
                    </h2>

                    {/* Форма */}
                    <div className={cl.addCommentForm}>
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Напишите ваш комментарий…"
                            className={cl.commentTextarea}
                            rows={4}
                            maxLength={2000}
                        />
                        <div className={cl.commentActions}>
                            <span className={cl.charCounter}>{newComment.length}/2000</span>
                            <button
                                onClick={handleAddComment}
                                disabled={!newComment.trim() || commentLoading}
                                className={cl.addCommentButton}
                            >
                                {commentLoading ? 'Добавляю…' : 'Добавить комментарий'}
                            </button>
                        </div>
                    </div>

                    {/* Список */}
                    {comments.length === 0 ? (
                        <div className={cl.noComments}>
                            Комментариев пока нет. Будьте первым!
                        </div>
                    ) : (
                        <div className={cl.commentsList}>
                            {comments.map(comment => (
                                <CommentItem key={comment.id} comment={comment}/>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};


// ----- Комментарий -----
const CommentItem = ({ comment }) => {
    const [authorAvatar, setAuthorAvatar] = useState(null);
    const [avatarFailed, setAvatarFailed] = useState(false);

    useEffect(() => {
        const loadAvatar = async () => {
            try {
                const info = await UserService.getUserById(comment.author_id);
                if (info?.data?.photoLink) {
                    setAuthorAvatar(`${FOLDER_PROFILE}${info.data.photoLink}`);
                }
            } catch (err) {
                logger.error('Ошибка загрузки аватара комментария:', err);
            }
        };
        if (comment.author_id) loadAvatar();
    }, [comment.author_id]);

    const showImg = authorAvatar && !avatarFailed;
    const initial = (comment.author_name?.[0] || '?').toUpperCase();

    return (
        <div className={cl.commentItem}>
            <div className={cl.commentAvatarWrap}>
                {showImg ? (
                    <img
                        src={authorAvatar}
                        alt={comment.author_name}
                        className={cl.commentAvatar}
                        onError={() => setAvatarFailed(true)}
                    />
                ) : (
                    <div className={cl.commentAvatarFallback}>{initial}</div>
                )}
            </div>
            <div className={cl.commentBody}>
                <div className={cl.commentHead}>
                    <span className={cl.commentAuthor}>{comment.author_name}</span>
                    <span className={cl.commentDate}>
                        {AdminService.formatDate(comment.created_at)}
                    </span>
                </div>
                <div className={cl.commentContent}>
                    {(comment.content || '').split('\n').map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>
            </div>
        </div>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default ArticleView;
